import { useCallback } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { create } from 'zustand';

// --- Types ---

type ToastVariant = 'default' | 'success' | 'error' | 'warning';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastStore {
  toasts: ToastItem[];
  addToast: (toast: ToastItem) => void;
  removeToast: (id: string) => void;
}

// --- Zustand Store (inline) ---

const MAX_TOASTS = 3;

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) =>
    set((state) => {
      const next = [...state.toasts, toast];
      // Keep only the most recent MAX_TOASTS
      return { toasts: next.slice(-MAX_TOASTS) };
    }),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));

// --- Hook ---

let toastCounter = 0;

export function useToast() {
  const addToast = useToastStore((s) => s.addToast);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${++toastCounter}-${Date.now()}`;
      addToast({
        id,
        title: options.title,
        description: options.description,
        variant: options.variant ?? 'default',
      });
    },
    [addToast],
  );

  return { toast };
}

// --- Variant border colors ---

const variantBorderColor: Record<ToastVariant, string> = {
  default: '#3b82f6',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// --- Provider ---

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  return (
    <ToastPrimitive.Provider swipeDirection="down" duration={4000}>
      {children}

      {toasts.map((t) => (
        <ToastPrimitive.Root
          key={t.id}
          className="toast-root"
          style={{
            borderLeftColor: variantBorderColor[t.variant],
          }}
          onOpenChange={(open) => {
            if (!open) removeToast(t.id);
          }}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-1 min-w-0">
              <ToastPrimitive.Title className="text-sm font-semibold text-gray-900 leading-tight">
                {t.title}
              </ToastPrimitive.Title>
              {t.description && (
                <ToastPrimitive.Description className="text-xs text-gray-500 mt-0.5 leading-snug">
                  {t.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close
              className="shrink-0 p-1 rounded-lg text-gray-400 active:bg-gray-100 transition-colors"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          </div>
        </ToastPrimitive.Root>
      ))}

      <ToastPrimitive.Viewport className="fixed bottom-0 left-0 right-0 z-[9999] flex flex-col-reverse gap-2 p-4 pb-safe max-w-md mx-auto outline-none" />
    </ToastPrimitive.Provider>
  );
}
